import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import * as signalR from "@microsoft/signalr";

import {
  getMessages,
  getConversations,
} from "../api/chat";

import ChatSidebar from "../components/ChatSidebar";
import ConversationHeader from "../components/ConversationHeader";
import ChatMessageList from "../components/ChatMessageList";
import ChatMessageInput from "../components/ChatMessageInput";

const API_BASE = process.env.REACT_APP_API_BASE;

export default function ChatPage() {
  const { conversationId } = useParams();
  const navigate = useNavigate();

  /* ---------------- STATE ---------------- */
  const [conversations, setConversations] = useState([]);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [typingUser, setTypingUser] = useState(null);

  const [onlineUsers, setOnlineUsers] = useState({});
  const [unreadCounts, setUnreadCounts] = useState({});
  const [connection, setConnection] = useState(null);

  const myUserId = localStorage.getItem("userId") || "";
  const bottomRef = useRef(null);

  /* ---------------- LOAD CONVERSATIONS ---------------- */
  useEffect(() => {
    getConversations()
      .then((data) => {
        const list = data?.items || data || [];
        setConversations(list);

        // Falls keine conversationId vorhanden → erste Konversation öffnen
        if (!conversationId && list.length > 0) {
          navigate(`/chat/${list[0].id}`, { replace: true });
        }
      })
      .catch(console.error);
  }, [conversationId, navigate]);

  /* ---------------- LOAD MESSAGES ---------------- */
  useEffect(() => {
    if (!conversationId) {
      setMessages([]);
      return;
    }

    getMessages(conversationId)
      .then((data) => {
        setMessages(data?.items || []);
        setUnreadCounts((prev) => ({
          ...prev,
          [conversationId]: 0,
        }));
      })
      .catch(console.error);
  }, [conversationId]);

  /* ---------------- SIGNALR CONNECTION ---------------- */
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) return;

    const conn = new signalR.HubConnectionBuilder()
      .withUrl(`${API_BASE}/chatHub`, {
        accessTokenFactory: () => token,
      })
      .withAutomaticReconnect()
      .build();

    let isMounted = true;

    conn
      .start()
      .then(async () => {
        if (!isMounted) return;

        if (conversationId) {
          await conn.invoke("JoinConversation", conversationId);
        }

        try {
          const online = await conn.invoke("GetOnlineUsers");
          setOnlineUsers(online || {});
        } catch {}
      })
      .catch(console.error);

    /* -------- EVENTS -------- */

    conn.on("ReceiveMessage", (msg) => {
      const cid = msg.conversationId || msg.ConversationId;

      if (cid === conversationId) {
        setMessages((prev) => [...prev, msg]);
      } else {
        setUnreadCounts((prev) => ({
          ...prev,
          [cid]: (prev[cid] || 0) + 1,
        }));
      }
    });

    conn.on("Typing", (cid, uid) => {
      if (cid === conversationId && uid !== myUserId) {
        setTypingUser(uid);
        setTimeout(() => setTypingUser(null), 1500);
      }
    });

    conn.on("UserOnline", (uid) =>
      setOnlineUsers((prev) => ({ ...prev, [uid]: true }))
    );

    conn.on("UserOffline", (uid) =>
      setOnlineUsers((prev) => ({ ...prev, [uid]: false }))
    );

    setConnection(conn);

    return () => {
      isMounted = false;
      conn.stop();
    };
  }, [conversationId, myUserId]);

  /* ---------------- ACTIONS ---------------- */
  const sendMessage = () => {
    if (!connection || !text.trim()) return;

    connection
      .invoke("SendMessage", conversationId, text.trim())
      .catch(console.error);

    setText("");
  };

  const handleTyping = (e) => {
    setText(e.target.value);
    if (connection && conversationId) {
      connection.invoke("UserTyping", conversationId).catch(() => {});
    }
  };

  /* ---------------- EMPTY STATE ---------------- */
  if (!conversationId) {
    return (
      <div className="h-screen flex items-center justify-center text-gray-500">
        Sélectionnez une conversation
      </div>
    );
  }

  /* ---------------- RENDER ---------------- */
  return (
    <div className="flex h-screen bg-gray-100 overflow-hidden">
      {/* SIDEBAR */}
      <ChatSidebar
        conversations={conversations}
        conversationId={conversationId}
        myUserId={myUserId}
        onlineUsers={onlineUsers}
        unreadCounts={unreadCounts}
      />

      {/* CHAT MAIN */}
      <div className="flex-1 flex flex-col">
        <ConversationHeader
          conversationId={conversationId}
          conversations={conversations}
          typingUser={typingUser}
          onlineUsers={onlineUsers}
        />

        <ChatMessageList
          messages={messages}
          myUserId={myUserId}
          bottomRef={bottomRef}
        />

        <ChatMessageInput
          text={text}
          onChange={handleTyping}
          onSend={sendMessage}
        />
      </div>
    </div>
  );
}
